import { useEffect, useState } from 'react';
import {
  createFrontendPlugin,
  PageBlueprint,
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { InfoCard, WarningPanel, Header, Page, Content } from '@backstage/core-components';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  Link,
  MenuItem,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import PublishIcon from '@material-ui/icons/Publish';
import { staticSitePromotePermission } from './conduitPermissions';

type Site = {
  product_slug: string;
  product_name: string;
  sandbox_url: string;
  prod_url: string;
  jira_ticket: string;
  promoted: boolean;
};

function PromoteForm() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [sites, setSites] = useState<Site[]>([]);
  const [prodConfigured, setProdConfigured] = useState(true);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedSlug, setSelectedSlug] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    runUrl: string;
    prodUrl: string;
    workspaceName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = sites.find(s => s.product_slug === selectedSlug);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('conduit-static-site');
        const response = await fetchApi.fetch(`${baseUrl}/sites`);
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          setLoadError(data.error || `Could not load sites (${response.status})`);
        } else {
          setSites(data.sites ?? []);
          setProdConfigured(Boolean(data.prodConfigured));
        }
      } catch (requestError: any) {
        if (!cancelled) setLoadError(requestError.message || 'Network error');
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    if (!selected) {
      setError('Choose a sandbox site to promote.');
      return;
    }
    if (!/^https:\/\//.test(embedUrl.trim())) {
      setError('Paste the report link, beginning with https://, to confirm what goes live.');
      return;
    }

    setSubmitting(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('conduit-static-site');
      const response = await fetchApi.fetch(`${baseUrl}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_slug: selected.product_slug,
          embed_url: embedUrl.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) setError(data.error || `Request failed (${response.status})`);
      else setResult(data);
    } catch (requestError: any) {
      setError(requestError.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!prodConfigured && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Production promotion is not fully configured yet. Sites are listed for
          reference, but promoting will fail until the production account is wired
          (run role, hosted zone, and certificate).
        </Alert>
      )}

      <InfoCard title="Promote a sandbox site to production">
        {loadingSites && <CircularProgress size={22} />}
        {loadError && (
          <Alert severity="error">
            <strong>We could not load your sandbox sites.</strong> {loadError}
          </Alert>
        )}

        {!loadingSites && !loadError && sites.length === 0 && (
          <Typography>
            No sandbox sites were found. Create one with the Static Site function
            first, then return here to promote it.
          </Typography>
        )}

        {!loadingSites && sites.length > 0 && (
          <form onSubmit={submit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Choose a site you have validated in the sandbox. Everything is
                  copied from that site; you only confirm the report link before
                  it goes live at its production address.
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Which sandbox site do you want to promote?"
                  value={selectedSlug}
                  onChange={event => {
                    setSelectedSlug(event.target.value);
                    setResult(null);
                    setError(null);
                  }}
                  helperText="Only sites created in the sandbox appear here."
                  fullWidth
                  required
                >
                  {sites.map(site => (
                    <MenuItem key={site.product_slug} value={site.product_slug}>
                      {site.product_name}
                      {site.promoted ? ' (already promoted)' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {selected && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Sandbox address (validated)"
                      value={selected.sandbox_url}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Production address it will go live at"
                      value={selected.prod_url}
                      InputProps={{ readOnly: true }}
                      helperText="Taken from the production address recorded when the sandbox site was created."
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Jira ticket"
                      value={selected.jira_ticket}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>

                  {selected.promoted && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        A production workspace already exists for this site.
                        Promoting again updates it with the details below.
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      label="Confirm the report or page link that should go live"
                      placeholder="https://app.powerbi.com/reportEmbed?..."
                      value={embedUrl}
                      onChange={event => setEmbedUrl(event.target.value)}
                      helperText="For security the sandbox report link is not stored where we can read it back, so paste it once more to confirm exactly what goes to production. It must begin with https://."
                      required
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={18} /> : <PublishIcon />}
                    >
                      {submitting ? 'Promoting to production...' : 'Promote to Production'}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        )}
      </InfoCard>

      {result && (
        <Alert severity="success" style={{ marginTop: 16 }}>
          Promotion of <strong>{result.workspaceName}</strong> was submitted. The
          production site at <strong>{result.prodUrl}</strong> is now being
          prepared.{' '}
          <Link href={result.runUrl} target="_blank" rel="noopener">
            Follow its progress
          </Link>
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} style={{ marginTop: 16 }}>
          <strong>We could not promote this site.</strong> {error}
        </Alert>
      )}
    </>
  );
}

function PromotePage() {
  return (
    <Page themeId="tool">
      <Header
        title="Promote to Production"
        subtitle="Take a report site you validated in the sandbox and stand up its production twin"
      />
      <Content>
        <RequirePermission
          permission={staticSitePromotePermission}
          errorPage={
            <WarningPanel title="Access Denied" severity="error">
              Promoting a site to production requires the{' '}
              <strong>Platform Admin</strong> role.
            </WarningPanel>
          }
        >
          <PromoteForm />
        </RequirePermission>
      </Content>
    </Page>
  );
}

export default createFrontendPlugin({
  pluginId: 'conduit-static-site-promote',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/static-site-promote',
        title: 'Promote to Production',
        icon: <PublishIcon />,
        loader: async () => <PromotePage />,
      },
    }),
  ],
});
